import React, { useEffect, useRef, useState } from 'react';
import { ConfirmDialogService, ToastService } from '@iamsoftware/react-hooks';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';

import {
  AgreementService,
  HsmKeyOption,
  HsmSignError
} from 'src/service/AgreementService';
import { DocumentSignService } from 'src/service/DocumentSignService';
import {
  EmploymentContractAgreementDetail,
  EmploymentContractAgreementRow,
  EmploymentContractService,
  EmploymentContractSigningOption
} from './EmploymentContractService';

export type EmploymentContractSigningAction = 'employer-sign' | 'send-email';

type SigningMethod = 'hsm' | 'usb-token';

type Props = {
  visible: boolean;
  action: EmploymentContractSigningAction;
  agreement?: EmploymentContractAgreementRow | null;
  onHide: () => void;
  onSuccess: () => void | Promise<void>;
};

const REFRESH_ERROR_CODES = new Set([
  'NOT_SIGNING_TURN',
  'DOCUMENT_VERSION_CHANGED',
  'SIGNING_IN_PROGRESS',
  'ALREADY_SIGNED',
  'HSM_OUTCOME_UNKNOWN'
]);

const extractError = (error: any, fallback: string) =>
  error?.response?.data?.error
  || error?.response?.data?.errors
  || error?.response?.data?.errorMessage
  || error?.response?.data?.message
  || error?.error
  || error?.errorMessage
  || error?.message
  || fallback;

const isSigned = (party: any) =>
  party?.partyStatusId === 'AGT_PTY_SIGNED' || party?.statusId === 'AGT_PTY_SIGNED';

const getHsmErrorMessage = (error: HsmSignError) => {
  const message = error.message;
  return error.referenceId ? `${message} Mã tra cứu: ${error.referenceId}` : message;
};

const getHsmKeyLabel = (key: HsmKeyOption) => {
  const createdDate = key.createdAt ? new Date(key.createdAt) : null;
  const createdLabel = createdDate && !Number.isNaN(createdDate.getTime())
    ? createdDate.toLocaleString('vi-VN')
    : key.createdAt;
  return [key.keyId, key.algorithm, createdLabel].filter(Boolean).join(' · ');
};

const fetchSigningContext = async (agreementId: string) => {
  const response = await EmploymentContractService.getAgreement(agreementId, false);
  if (response?.success === 'N') {
    throw new Error(response?.error || 'Không tải được thông tin hợp đồng');
  }
  const [employer, employee] = (response?.agreementParties || []).slice(0, 2);
  return { response, employer, employee };
};

const buildPushParams = (signResult: any, signer: any) => {
  const { signedAgreements, signedFiles, signedFilesBase64 } = signResult?.data || {};
  const pushParams: any = {
    signedBy: 'SELLER',
    agreementId: signer.agreementId,
    partyId: signer.partyId,
    roleTypeId: signer.roleTypeId || 'AgreementSigner',
    referenceNumber: null,
    signingSecretCode: '',
    signedByName: signResult?.data?.signedByName || ''
  };

  if (Array.isArray(signedAgreements) && signedAgreements.length > 0) {
    pushParams.signedFiles = signedAgreements.flatMap((signedAgreement: any) =>
      (signedAgreement.files || []).map((file: any) => ({
        agreementContentId: file.agreementContentId,
        base64FileData: file.base64FileData
      }))
    );
  } else if (Array.isArray(signedFiles) && signedFiles.length > 0) {
    pushParams.signedFiles = signedFiles.map((file: any) => ({
      agreementContentId: file.agreementContentId,
      base64FileData: file.base64FileData
    }));
  } else if (Array.isArray(signedFilesBase64) && signedFilesBase64.length > 0) {
    pushParams.base64FileData = signedFilesBase64[0];
  }

  return pushParams;
};

export const EmploymentContractSigningDialog: React.FC<Props> = ({
  visible,
  action,
  agreement,
  onHide,
  onSuccess
}) => {
  const [detail, setDetail] = useState<EmploymentContractAgreementDetail | null>(null);
  const [employer, setEmployer] = useState<any>(null);
  const [selectedMethod, setSelectedMethod] = useState<SigningMethod | null>(null);
  const [hsmUsername, setHsmUsername] = useState('');
  const [hsmPassword, setHsmPassword] = useState('');
  const [hsmKeys, setHsmKeys] = useState<HsmKeyOption[]>([]);
  const [selectedHsmKeyId, setSelectedHsmKeyId] = useState('');
  const [hsmKeysLoaded, setHsmKeysLoaded] = useState(false);
  const [loadingHsmKeys, setLoadingHsmKeys] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const onHideRef = useRef(onHide);
  const submitEmailRef = useRef<(employerParty: any, employeeParty: any) => void>(() => undefined);
  const isSigning = action === 'employer-sign';
  onHideRef.current = onHide;

  useEffect(() => {
    if (!visible || !agreement?.agreementId) {
      setDetail(null);
      setEmployer(null);
      setSelectedMethod(null);
      setHsmUsername('');
      setHsmPassword('');
      setHsmKeys([]);
      setSelectedHsmKeyId('');
      setHsmKeysLoaded(false);
      setLoadingHsmKeys(false);
      return;
    }

    let active = true;
    submittingRef.current = false;
    setSubmitting(false);
    setLoadingDetail(true);
    setSelectedMethod(null);
    setHsmUsername('');
    setHsmPassword('');
    setHsmKeys([]);
    setSelectedHsmKeyId('');
    setHsmKeysLoaded(false);
    setLoadingHsmKeys(false);

    fetchSigningContext(agreement.agreementId)
      .then(context => {
        if (!active) return;
        setDetail(context.response);
        setEmployer(context.employer);

        if (!isSigning) {
          ConfirmDialogService.setProps({
            header: 'Xác nhận gửi email',
            message: 'Bạn xác nhận gửi email mời ký cho bên B?',
            icon: 'pi pi-question-circle',
            acceptClassName: 'p-button-primary',
            acceptLabel: 'Gửi email',
            rejectLabel: 'Không',
            accept: () => submitEmailRef.current(context.employer, context.employee),
            reject: () => onHideRef.current(),
            onHide: () => {
              if (!submittingRef.current) onHideRef.current();
            }
          });
        }
      })
      .catch(error => {
        if (!active) return;
        ToastService.error(extractError(error, 'Không tải được thông tin hợp đồng'));
        onHideRef.current();
      })
      .finally(() => {
        if (active) setLoadingDetail(false);
      });

    return () => {
      active = false;
    };
  }, [agreement?.agreementId, isSigning, visible]);

  const handleUsbSign = async () => {
    if (!agreement?.agreementId || !employer?.partyId) {
      throw new Error('Không tìm thấy thông tin bên sử dụng lao động');
    }
    if (isSigned(employer)) {
      throw new Error('Bên A đã ký hợp đồng');
    }

    const signer = {
      ...employer,
      agreementId: agreement.agreementId
    };
    const signResult = await DocumentSignService.sign({
      agreementId: agreement.agreementId,
      partyId: signer.partyId,
      roleTypeId: signer.roleTypeId || 'AgreementSigner'
    });
    if (!signResult?.success || !signResult?.data) {
      try {
        await AgreementService.updateSigningError({
          agreementId: agreement.agreementId,
          partyId: signer.partyId,
          roleTypeId: signer.roleTypeId || 'AgreementSigner',
          referenceNumber: null,
          signingSecretCode: ''
        });
      } catch {
        // Giữ lỗi gốc từ Signing Agent.
      }
      throw new Error(signResult?.errorMessage || 'Ký hợp đồng không thành công');
    }

    await AgreementService.pushSignedData(buildPushParams(signResult, signer));
    ToastService.success('Bên A đã ký hợp đồng bằng USB Token thành công');
  };

  const handleHsmSign = async () => {
    if (!agreement?.agreementId || !detail?.documentVersion) {
      throw new Error('Không tìm thấy phiên bản tài liệu để ký HSM');
    }
    if (!hsmUsername.trim() || !hsmPassword || !selectedHsmKeyId) {
      throw new Error('Vui lòng nhập credential và chọn khóa HSM');
    }

    await AgreementService.signHsm(agreement.agreementId, {
      expectedDocumentVersion: detail.documentVersion,
      hsmUsername: hsmUsername.trim(),
      hsmPassword,
      keyId: selectedHsmKeyId
    });
    ToastService.success('Bên A đã ký hợp đồng bằng HSM thành công');
  };

  const handleLoadHsmKeys = async () => {
    if (!agreement?.agreementId || !detail?.documentVersion) {
      ToastService.error('Không tìm thấy phiên bản tài liệu để tải khóa HSM');
      return;
    }
    if (!hsmUsername.trim() || !hsmPassword) {
      ToastService.error('Vui lòng nhập tài khoản và mật khẩu HSM trước');
      return;
    }

    setLoadingHsmKeys(true);
    setHsmKeys([]);
    setSelectedHsmKeyId('');
    setHsmKeysLoaded(false);
    try {
      const response = await AgreementService.listHsmKeys(agreement.agreementId, {
        expectedDocumentVersion: detail.documentVersion,
        hsmUsername: hsmUsername.trim(),
        hsmPassword
      });
      const keys = Array.isArray(response?.keys) ? response.keys : [];
      setHsmKeys(keys);
      setSelectedHsmKeyId(keys.length === 1 ? keys[0].keyId : '');
      setHsmKeysLoaded(true);
      ToastService.success(`Đã tải ${keys.length} khóa HSM đang hoạt động`);
    } catch (error: any) {
      const hsmError = AgreementService.getHsmSignError(error);
      if (hsmError.code === 'HSM_AUTH_FAILED') setHsmPassword('');
      ToastService.error(getHsmErrorMessage(hsmError));
    } finally {
      setLoadingHsmKeys(false);
    }
  };

  const refreshSigningState = async (closeAfterRefresh: boolean) => {
    await onSuccess();
    if (closeAfterRefresh || !agreement?.agreementId) {
      onHide();
      return;
    }
    try {
      const context = await fetchSigningContext(agreement.agreementId);
      setDetail(context.response);
      setEmployer(context.employer);
      setSelectedMethod(null);
      setHsmKeys([]);
      setSelectedHsmKeyId('');
      setHsmKeysLoaded(false);
    } catch (error: any) {
      ToastService.error(extractError(error, 'Không tải lại được trạng thái hợp đồng'));
      onHide();
    }
  };

  const submitSigning = async () => {
    if (submittingRef.current || !selectedMethod) return;
    if (selectedMethod === 'hsm' && (!hsmUsername.trim() || !hsmPassword || !selectedHsmKeyId)) {
      ToastService.error('Vui lòng nhập credential, tải danh sách và chọn khóa HSM');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (selectedMethod === 'hsm') {
        try {
          await handleHsmSign();
        } catch (error: any) {
          const hsmError = AgreementService.getHsmSignError(error);
          setHsmPassword('');
          if (hsmError.code === 'HSM_KEY_INVALID') {
            setHsmKeys([]);
            setSelectedHsmKeyId('');
            setHsmKeysLoaded(false);
          }
          ToastService.error(getHsmErrorMessage(hsmError));

          if (REFRESH_ERROR_CODES.has(hsmError.code)) {
            if (hsmError.code === 'HSM_OUTCOME_UNKNOWN') setSelectedMethod(null);
            await refreshSigningState(hsmError.code === 'ALREADY_SIGNED');
          }
          return;
        }
      } else {
        await handleUsbSign();
      }

      setHsmPassword('');
      setHsmKeys([]);
      setSelectedHsmKeyId('');
      setHsmKeysLoaded(false);
      await onSuccess();
      onHide();
    } catch (error: any) {
      ToastService.error(extractError(error, 'Có lỗi khi ký hợp đồng'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const submitEmail = async (employerParty: any, employeeParty: any) => {
    if (submittingRef.current || !agreement?.agreementId) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (!isSigned(employerParty)) {
        throw new Error('Bên A phải ký trước khi gửi email cho bên B');
      }
      if (isSigned(employeeParty)) {
        throw new Error('Bên B đã ký hợp đồng');
      }

      const response = await AgreementService.publishSigning(agreement.agreementId);
      if ((response?.notificationSummary?.failed || 0) > 0) {
        throw new Error('Đã mở lượt ký cho bên B nhưng email chưa gửi được');
      }
      ToastService.success('Đã gửi email cho bên B');
      await onSuccess();
    } catch (error: any) {
      ToastService.error(extractError(error, 'Có lỗi khi gửi email'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      onHide();
    }
  };
  submitEmailRef.current = submitEmail;

  const usbOption: EmploymentContractSigningOption = detail?.signingOptions?.usbToken || {
    enabled: false
  };
  const hsmOption: EmploymentContractSigningOption = detail?.signingOptions?.hsm || {
    enabled: false,
    requiresCredentials: true
  };
  const selectedOption = selectedMethod === 'hsm' ? hsmOption : usbOption;
  const busy = submitting || loadingHsmKeys;
  const hsmKeyOptions = hsmKeys.map(key => ({
    label: getHsmKeyLabel(key),
    value: key.keyId
  }));
  const submitDisabled = loadingDetail
    || !selectedMethod
    || !selectedOption.enabled
    || (selectedMethod === 'hsm' && (!hsmUsername.trim() || !hsmPassword || !selectedHsmKeyId));

  const selectMethod = (method: SigningMethod, option: EmploymentContractSigningOption) => {
    if (busy || !option.enabled) return;
    setSelectedMethod(method);
    if (method !== 'hsm') {
      setHsmUsername('');
      setHsmPassword('');
      setHsmKeys([]);
      setSelectedHsmKeyId('');
      setHsmKeysLoaded(false);
    }
  };

  const renderMethodCard = (
    method: SigningMethod,
    option: EmploymentContractSigningOption,
    title: string,
    icon: string
  ) => {
    const selected = selectedMethod === method;
    return (
      <button
        type="button"
        className={`w-full text-left p-2 border-round border-2 surface-card transition-colors transition-duration-150 ${selected ? 'border-primary' : 'surface-border'}`}
        style={{
          cursor: option.enabled && !busy ? 'pointer' : 'not-allowed',
          opacity: option.enabled ? 1 : 0.62,
          background: selected ? 'var(--primary-50, #eff6ff)' : undefined,
          minHeight: '3.75rem'
        }}
        disabled={!option.enabled || busy}
        aria-pressed={selected}
        onClick={() => selectMethod(method, option)}
      >
        <span className="flex align-items-center gap-3">
          <span className={`flex align-items-center justify-content-center border-circle ${selected ? 'bg-primary text-primary-color' : 'surface-100 text-600'}`}
            style={{ width: '2.5rem', height: '2.5rem', flex: '0 0 auto' }}>
            <i className={`pi ${icon} text-xl`} />
          </span>
          <span className="flex-1">
            <span className="flex align-items-center justify-content-between gap-2">
              <span className="font-semibold text-900">{title}</span>
              <i className={`pi ${selected ? 'pi-check-circle text-primary' : 'pi-circle text-400'}`} />
            </span>
            {!option.enabled && option.message && (
              <span className="block text-sm text-red-500 mt-2">
                {option.message}
              </span>
            )}
          </span>
        </span>
      </button>
    );
  };

  const closeSigningDialog = () => {
    if (submittingRef.current || loadingHsmKeys) return;
    setHsmUsername('');
    setHsmPassword('');
    setHsmKeys([]);
    setSelectedHsmKeyId('');
    setHsmKeysLoaded(false);
    setSelectedMethod(null);
    onHide();
  };

  if (!isSigning) return null;

  return (
    <Dialog
      header="Chọn phương thức ký hợp đồng"
      visible={visible}
      modal
      draggable={false}
      closable={!busy}
      closeOnEscape={!busy}
      dismissableMask={false}
      style={{ width: 'min(38rem, calc(100vw - 2rem))' }}
      contentStyle={{ padding: '0 1rem 1rem' }}
      onHide={closeSigningDialog}
      footer={(
        <div className="flex justify-content-center gap-2">
          <Button
            label="Hủy"
            icon="pi pi-times"
            outlined
            disabled={busy}
            onClick={closeSigningDialog}
          />
          <Button
            label={selectedMethod === 'hsm'
              ? 'Ký HSM'
              : (selectedMethod === 'usb-token' ? 'Ký USB Token' : 'Chọn phương thức ký')}
            icon={selectedMethod === 'hsm' ? 'pi pi-shield' : 'pi pi-key'}
            loading={submitting}
            disabled={submitDisabled}
            onClick={submitSigning}
          />
        </div>
      )}
    >
      {loadingDetail ? (
        <div className="flex flex-column align-items-center justify-content-center gap-3 py-6 text-600">
          <i className="pi pi-spin pi-spinner text-3xl" />
          <span>Đang tải điều kiện ký hợp đồng...</span>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <div className="grid m-0">
            <div className="col-12 md:col-6 p-1">
              {renderMethodCard(
                'hsm',
                hsmOption,
                'Ký HSM',
                'pi-shield'
              )}
            </div>
            <div className="col-12 md:col-6 p-1">
              {renderMethodCard(
                'usb-token',
                usbOption,
                'Ký USB Token',
                'pi-key'
              )}
            </div>
          </div>

          {selectedMethod === 'hsm' && hsmOption.enabled && (
            <div className="surface-50 border-1 surface-border border-round p-3 flex flex-column gap-3">
              <div>
                <label htmlFor="employment-contract-hsm-username" className="block font-medium text-900 mb-2">
                  Tài khoản HSM <span className="text-red-500">*</span>
                </label>
                <InputText
                  id="employment-contract-hsm-username"
                  value={hsmUsername}
                  onChange={event => {
                    setHsmUsername(event.target.value);
                    setHsmKeys([]);
                    setSelectedHsmKeyId('');
                    setHsmKeysLoaded(false);
                  }}
                  autoComplete="username"
                  className="w-full"
                  disabled={busy}
                  placeholder="Nhập username của HSM API"
                />
              </div>
              <div>
                <label htmlFor="employment-contract-hsm-password" className="block font-medium text-900 mb-2">
                  Mật khẩu HSM <span className="text-red-500">*</span>
                </label>
                <Password
                  id="employment-contract-hsm-password"
                  value={hsmPassword}
                  onChange={event => {
                    setHsmPassword(event.target.value);
                    setHsmKeys([]);
                    setSelectedHsmKeyId('');
                    setHsmKeysLoaded(false);
                  }}
                  feedback={false}
                  toggleMask
                  autoComplete="off"
                  inputClassName="w-full"
                  className="w-full"
                  disabled={busy}
                  placeholder="Nhập password của HSM API"
                />
              </div>
              <div className="flex justify-content-end">
                <Button
                  type="button"
                  label={hsmKeysLoaded ? 'Tải lại danh sách khóa' : 'Tải danh sách khóa'}
                  icon="pi pi-refresh"
                  outlined
                  loading={loadingHsmKeys}
                  disabled={submitting || !hsmUsername.trim() || !hsmPassword}
                  onClick={handleLoadHsmKeys}
                />
              </div>
              {hsmKeysLoaded && (
                <div>
                  <label htmlFor="employment-contract-hsm-key" className="block font-medium text-900 mb-2">
                    Khóa ký HSM <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    inputId="employment-contract-hsm-key"
                    value={selectedHsmKeyId}
                    options={hsmKeyOptions}
                    onChange={event => setSelectedHsmKeyId(event.value)}
                    optionLabel="label"
                    optionValue="value"
                    filter
                    showClear
                    className="w-full"
                    disabled={busy}
                    placeholder="Chọn keyId dùng để ký"
                    emptyMessage="Không có khóa HSM đang hoạt động"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
